import { Controller } from "@hotwired/stimulus"
import { loadStripe } from "@stripe/stripe-js"

export default class extends Controller {
  static targets = ["paymentForm", "selectedAmount", "cardElement", "cardErrors", "submitButton",
                     "promoCodeInput", "promoCodeSuccess", "promoCodeError", "originalPrice",
                     "finalPrice", "discountAmount", "priceDisplay"]
  static values = { publishableKey: String }

  async connect() {
    this.stripe = await loadStripe(this.publishableKeyValue)
    this.elements = this.stripe.elements()
    this.cardElement = this.elements.create('card', {
      hidePostalCode: true
    })
    this.cardElement.mount(this.cardElementTarget)

    this.cardElement.on('change', (event) => {
      if (event.error) {
        this.cardErrorsTarget.textContent = event.error.message
      } else {
        this.cardErrorsTarget.textContent = ''
      }
    })
  }

  selectTicket(event) {
    const ticketId = event.currentTarget.dataset.ticketId
    const ticketPrice = event.currentTarget.dataset.ticketPrice
    const ticketName = event.currentTarget.dataset.ticketName

    this.selectedTicketId = ticketId
    this.selectedTicketPrice = parseInt(ticketPrice)
    this.selectedTicketName = ticketName
    this.appliedPromoCode = null
    this.discountAmount = 0

    this.selectedAmountTarget.textContent = `${ticketName} - ¥${this.selectedTicketPrice.toLocaleString()}`
    this.priceDisplayTarget.style.display = 'none'
    this.promoCodeInputTarget.value = ''
    this.promoCodeSuccessTarget.style.display = 'none'
    this.promoCodeErrorTarget.style.display = 'none'

    this.paymentFormTarget.style.display = 'block'
    this.paymentFormTarget.scrollIntoView({ behavior: 'smooth' })
  }

  async submit(event) {
    event.preventDefault()

    if (!this.selectedTicketId) {
      this.showError('チケットを選択してください')
      return
    }

    // ローディング画面を表示
    this.showLoadingOverlay()

    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>処理中...'

    try {
      const response = await fetch(`/payments/purchase/${this.selectedTicketId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          promo_code: this.appliedPromoCode
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || '決済の準備に失敗しました')
      }

      const { error } = await this.stripe.confirmCardPayment(data.client_secret, {
        payment_method: {
          card: this.cardElement,
          billing_details: {
            name: data.customer_name
          }
        }
      })

      if (error) {
        // 決済失敗時はサーバーに UserTicket を作らせていないため、孤児は発生しない。
        this.showError(this.getJapaneseErrorMessage(error))
      } else {
        await this.confirmPurchase(data.payment_intent_id)
      }
    } catch (error) {
      this.showError(error.message)
    } finally {
      this.hideLoadingOverlay()
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.textContent = 'チケットを購入'
    }
  }

  async confirmPurchase(paymentIntentId) {
    try {
      const response = await fetch(`/payments/confirm_ticket_purchase/${paymentIntentId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      const data = await response.json()

      if (response.ok) {
        // 基本の成功メッセージ
        let successMessage = 'チケットの購入が完了しました！'

        // 仮予約確定結果がある場合は詳細メッセージを追加
        if (data.tentative_result) {
          const confirmed = data.tentative_result.confirmed || 0
          const canceled = data.tentative_result.canceled || 0

          if (confirmed > 0 || canceled > 0) {
            successMessage += '\n\n【仮予約の確定結果】\n'
            if (confirmed > 0) {
              successMessage += `✅ ${confirmed}件の仮予約が確定されました\n`
            }
            if (canceled > 0) {
              successMessage += `❌ ${canceled}件の仮予約がセッション不足でキャンセルされました`
            }
          }
        }

        // 複数行メッセージ対応のため、モーダルで表示
        this.showSuccessModal(successMessage)

        // 3秒後にリロード
        setTimeout(() => {
          window.location.reload()
        }, 3000)
      } else {
        this.showError(data.error || '購入の確認に失敗しました')
      }
    } catch (error) {
      this.showError('購入の確認に失敗しました')
    }
  }

  getJapaneseErrorMessage(error) {
    const errorMessages = {
      'card_declined': 'カードが拒否されました。別のカードをお試しください。',
      'expired_card': 'カードの有効期限が切れています。',
      'incorrect_cvc': 'セキュリティコードが正しくありません。',
      'processing_error': '決済処理中にエラーが発生しました。',
      'incorrect_number': 'カード番号が正しくありません。',
      'invalid_expiry_month': '有効期限の月が正しくありません。',
      'invalid_expiry_year': '有効期限の年が正しくありません。',
      'invalid_cvc': 'セキュリティコードが正しくありません。',
      'insufficient_funds': '残高不足です。',
      'generic_decline': 'カードが拒否されました。カード会社にお問い合わせください。'
    }

    return errorMessages[error.code] || error.message || '決済処理中にエラーが発生しました。'
  }

  showError(message) {
    this.showToast(message, 'error')
  }

  showSuccess(message) {
    this.showToast(message, 'success')
  }

  showSuccessModal(message) {
    // 既存のモーダルがあれば削除
    const existingModal = document.getElementById('tentativeResultModal')
    if (existingModal) {
      existingModal.remove()
    }

    // モーダルHTML作成
    const modalHtml = `
      <div class="modal fade" id="tentativeResultModal" tabindex="-1">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header bg-success text-white">
              <h5 class="modal-title">
                <i class="bi bi-check-circle me-2"></i>
                決済完了
              </h5>
              <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body">
              <div class="text-center py-3">
                <i class="bi bi-check-circle-fill text-success" style="font-size: 3rem;"></i>
                <div class="mt-3" style="white-space: pre-line;">${message}</div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-primary" data-bs-dismiss="modal">確認</button>
            </div>
          </div>
        </div>
      </div>
    `

    // モーダルをbodyに追加
    document.body.insertAdjacentHTML('beforeend', modalHtml)

    // Bootstrapモーダルを表示
    const modalElement = document.getElementById('tentativeResultModal')
    import('bootstrap').then(({ Modal }) => {
      const modal = new Modal(modalElement)
      modal.show()

      // モーダルが閉じられたら要素を削除
      modalElement.addEventListener('hidden.bs.modal', () => {
        modalElement.remove()
      })
    }).catch(() => {
      // フォールバック: bootstrapがロードできない場合は通常のアラート
      alert(message)
    })
  }

  showToast(message, type) {
    const toast = document.createElement('div')
    toast.className = `toast align-items-center text-white bg-${type === 'error' ? 'danger' : 'success'} border-0`
    toast.setAttribute('role', 'alert')
    toast.innerHTML = `
      <div class="d-flex">
        <div class="toast-body">${message}</div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast"></button>
      </div>
    `

    const container = document.querySelector('.toast-container') || this.createToastContainer()
    container.appendChild(toast)

    // Bootstrapを動的にインポート
    import('bootstrap').then(({ Toast }) => {
      const bsToast = new Toast(toast)
      bsToast.show()
    }).catch(() => {
      // Bootstrapが読み込めない場合はシンプルな表示
      toast.classList.add('show')
      setTimeout(() => {
        toast.remove()
      }, 3000)
    })

    toast.addEventListener('hidden.bs.toast', () => {
      toast.remove()
    })
  }

  createToastContainer() {
    const container = document.createElement('div')
    container.className = 'toast-container position-fixed top-0 end-0 p-3'
    container.style.zIndex = '1055'
    document.body.appendChild(container)
    return container
  }

  async applyPromoCode() {
    const promoCode = this.promoCodeInputTarget.value.trim()

    if (!promoCode) {
      this.promoCodeErrorTarget.textContent = 'プロモーションコードを入力してください'
      this.promoCodeErrorTarget.style.display = 'block'
      this.promoCodeSuccessTarget.style.display = 'none'
      return
    }

    if (!this.selectedTicketId) {
      this.promoCodeErrorTarget.textContent = 'チケットを選択してください'
      this.promoCodeErrorTarget.style.display = 'block'
      this.promoCodeSuccessTarget.style.display = 'none'
      return
    }

    try {
      const response = await fetch('/payments/validate_promo_code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          code: promoCode,
          ticket_id: this.selectedTicketId,
          amount: this.selectedTicketPrice
        })
      })

      const data = await response.json()

      if (response.ok && data.valid) {
        this.appliedPromoCode = promoCode
        this.discountAmount = data.discount_amount
        const finalPrice = this.selectedTicketPrice - this.discountAmount

        this.promoCodeSuccessTarget.textContent = `${data.display_discount} 適用されました`
        this.promoCodeSuccessTarget.style.display = 'block'
        this.promoCodeErrorTarget.style.display = 'none'

        this.originalPriceTarget.textContent = `¥${this.selectedTicketPrice.toLocaleString()}`
        this.discountAmountTarget.textContent = `-¥${this.discountAmount.toLocaleString()}`
        this.finalPriceTarget.textContent = `¥${finalPrice.toLocaleString()}`
        this.priceDisplayTarget.style.display = 'block'
      } else {
        this.appliedPromoCode = null
        this.discountAmount = 0
        this.promoCodeErrorTarget.textContent = data.error || '無効なプロモーションコードです'
        this.promoCodeErrorTarget.style.display = 'block'
        this.promoCodeSuccessTarget.style.display = 'none'

        this.priceDisplayTarget.style.display = 'none'
      }
    } catch (error) {
      this.promoCodeErrorTarget.textContent = 'エラーが発生しました'
      this.promoCodeErrorTarget.style.display = 'block'
      this.promoCodeSuccessTarget.style.display = 'none'
    }
  }

  checkPromoCode() {
    // Clear messages when user starts typing
    if (this.promoCodeInputTarget.value.trim() === '') {
      this.promoCodeSuccessTarget.style.display = 'none'
      this.promoCodeErrorTarget.style.display = 'none'
    }
  }

  showLoadingOverlay() {
    // 既存のオーバーレイがあれば削除
    const existingOverlay = document.getElementById('paymentLoadingOverlay')
    if (existingOverlay) {
      existingOverlay.remove()
    }

    // ローディングオーバーレイを作成
    const overlay = document.createElement('div')
    overlay.id = 'paymentLoadingOverlay'
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 9999;
    `

    overlay.innerHTML = `
      <div class="spinner-border text-light" style="width: 3rem; height: 3rem;" role="status">
        <span class="visually-hidden">処理中...</span>
      </div>
      <div class="text-white mt-3">
        <h5>決済処理中...</h5>
        <p class="mb-0">しばらくお待ちください</p>
        <p class="small mt-2">画面を閉じないでください</p>
      </div>
    `

    document.body.appendChild(overlay)
  }

  hideLoadingOverlay() {
    const overlay = document.getElementById('paymentLoadingOverlay')
    if (overlay) {
      overlay.remove()
    }
  }
}
